import React from 'react';
import { OrganizationTypes } from '../../../../../../primary-record-types';
import useMedicalProfileFiles from '../../../../../../hooks/api/useMedicalProfileFiles';
import useOrganizationMedicalProfileFolderId from '../../../../../../hooks/api/useOrganizationMedicalProfileFolderId';
import { ActionButtonLayout } from '../../../../../v2BaseComponents/ActionButtonLayout';
import { AnimateLoadedContent } from '../../../../../v2BaseComponents/Loader';
import { useTempFilesPicker } from '../../../../../v2BaseComponents/ImagePicker/TempFilePicker';
import usePersistFileToClientProfiles from '../../../../../../hooks/api/usePersistFileToClientProfiles';
import { getIntl } from '../../../../../../utils/intl';
import EmptyStateHint from '../../../../../shared/EmptyStateHint';
import Stack, { contentStackSpacing } from '../../../../../v2BaseComponents/Stack';
import FileCard from '../../../../../shared/FileCard';
import MainContentContainer from '../../../../../v2BaseComponents/MainContentContainer';
import { useProfilesTabNavigation } from '../../../../../../utils/appNavigationNames';

const intl = getIntl('client-record-organization-files', {
    addFile: 'Add Files',
});

export type ClientRecordOrganizationFilesOwnProps = {
    activeClientRecord: OrganizationTypes.OrganizationClientRecordForStatus<'active'>;
};

export type ClientRecordOrganizationFilesProps = ClientRecordOrganizationFilesOwnProps;

const ClientRecordFileList: React.FC<{ organizationFolderId: string } & ClientRecordOrganizationFilesProps> = ({
    activeClientRecord,
    organizationFolderId,
}) => {
    const { isLoadingProfileFiles, profileFiles } = useMedicalProfileFiles({
        medicalProfileId: activeClientRecord.clientMedicalProfileId,
        folderId: organizationFolderId,
    });
    const { navigate } = useProfilesTabNavigation();

    return (
        <AnimateLoadedContent isLoading={isLoadingProfileFiles}>
            {() =>
                profileFiles?.length ? (
                    <Stack vertical fullWidth space={contentStackSpacing}>
                        {profileFiles.map((file) => (
                            <FileCard
                                key={file.medicalProfileFileId}
                                file={file}
                                onPress={() => {
                                    navigate('file', {
                                        fileId: file.medicalProfileFileId,
                                        profileId: activeClientRecord.clientMedicalProfileId,
                                    });
                                }}
                            />
                        ))}
                    </Stack>
                ) : (
                    <EmptyStateHint variant="organization-client-files" />
                )
            }
        </AnimateLoadedContent>
    );
};

const ClientRecordOrganizationFiles: React.FC<ClientRecordOrganizationFilesProps> = (props) => {
    const [resetListKey, setResetListKey] = React.useState(0);
    const { isLoadingFolderId, folderId } = useOrganizationMedicalProfileFolderId({
        clientProfileId: props.activeClientRecord.clientMedicalProfileId,
    });
    const { isPersisting, persistFilesToClientProfiles } = usePersistFileToClientProfiles({
        onPersistSuccess: () => {
            setResetListKey((prev) => prev + 1);
        },
    });

    const addFileTempPicker = useTempFilesPicker({
        onPick: (files) => {
            persistFilesToClientProfiles({
                tempFiles: files.map((file) => ({
                    tempFileId: file.tempFileId,
                    fileName: file.originalName || file.name,
                    contentType: file.contentType,
                })),
                destinationMedicalProfileIds: [props.activeClientRecord.clientMedicalProfileId],
            });
        },
    });

    return (
        <AnimateLoadedContent isLoading={isLoadingFolderId}>
            {() => (
                <ActionButtonLayout
                    scrollable
                    actions={[
                        {
                            title: intl('addFile'),
                            onPress: addFileTempPicker.onPress,
                            icon: 'PlusCircle',
                            isDisabled: isPersisting,
                        },
                    ]}
                >
                    <MainContentContainer testID="organization-profile-files-content" style={{ minHeight: '100%' }}>
                        {folderId ? (
                            <ClientRecordFileList organizationFolderId={folderId} {...props} key={resetListKey} />
                        ) : (
                            <EmptyStateHint variant="organization-client-files" />
                        )}
                        {addFileTempPicker.E2EInput}
                    </MainContentContainer>
                </ActionButtonLayout>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(ClientRecordOrganizationFiles);
